import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  useParams, useNavigate, useLocation, Outlet
} from 'react-router-dom';
import { func } from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import {
  addErrorMessageAction, setTaskFulfilled, setTaskPending, updateSurveyOptionsAction, clearSurveyAction, initSurveyParticipantsAction
} from '../../actions';
import { getCourseUnitRealisations, getSurveyForEdit } from '../../api';
import {
  langType,
  surveyOptionsType,
  surveyTypeValues
} from '../../constants/types';
import ScrollToTop from '../ScrollToTop';
import { DEGREE_PROGRAM_NOT_SELECTED, routes } from '../../constants';
import {
  getCompiledPath, getRouteSuffix, getSubRouteForSurveyType, getTitlePeriods, isSurveyForDegreeProgram, parseTitle
} from '../../util';
import { getLocalizedResearchAgreement, withLang } from '../../i18n';
import { usePreviousLocation } from '../PreviousLocationProvider';
import useTitle from '../../hooks/useTitle';

const EditSurvey = ({
  surveyOptions,
  dispatchUpdate,
  dispatchClear,
  dispatchSetTaskPending,
  dispatchAddErrorMessage,
  dispatchSetTaskFulfilled,
  dispatchInitParticipants,
  translate: t
}) => {
  const [loaded, setLoaded] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation = usePreviousLocation();
  const { pathname } = location;

  const subRoutes = [
    getRouteSuffix(routes.surveys.hulSurveys.survey.edit.contents.questions),
    getRouteSuffix(routes.surveys.hulSurveys.survey.edit.contents.extraSurveyPart)
  ];
  const isInSubRoute = subRoutes.some((route) => location.pathname.endsWith(route));

  const isDegreeProgramSurveyTitleUninitialised = (survey) => isSurveyForDegreeProgram(survey.surveyType)
    && survey.degreeProgram?.code !== DEGREE_PROGRAM_NOT_SELECTED.code
    && !survey.degreeProgram?.name;

  React.useEffect(() => {
    const isReturningFromNestedRoute =
      previousLocation
      && !![
        getRouteSuffix(routes.surveys.hulSurveys.startSurvey.contents.questions),
        getRouteSuffix(routes.surveys.hulSurveys.startSurvey.contents.extraSurveyPart)
      ].find((route) => previousLocation.pathname.endsWith(route));

    if (!isReturningFromNestedRoute) {
      const fetchData = async () => {
        const titlePeriods = getTitlePeriods();
        dispatchClear();
        dispatchUpdate({ titlePeriods });
        dispatchSetTaskPending();

        try {
          const {
            survey,
            schedule: { startDate, endDate },
            messages,
            reminderSchedules,
            participants,
            schema
          } = await getSurveyForEdit(params.surveyId);
          if (survey.type === surveyTypeValues.DEGREE_PROGRAM_SURVEY && !survey.degreeProgram) {
            survey.degreeProgram = DEGREE_PROGRAM_NOT_SELECTED;
          }
          const courses = survey.courseRealisationIds?.length
            ? await getCourseUnitRealisations(survey.courseRealisationIds, params.surveyId)
            : [];
          const { titlePeriodName, titleExtraPart } = parseTitle(survey.title);
          const researchAgreement = getLocalizedResearchAgreement(survey.type);
          dispatchInitParticipants({ participants, courses, onlyManuallyAdded: true });
          dispatchUpdate({
            ...survey,
            researchAgreement,
            startDate,
            endDate,
            ...messages,
            schemaTitle: schema.title,
            surveyType: survey.type,
            reminderSchedules: reminderSchedules.map((schedule) => schedule.scheduledTime),
            ...(isSurveyForDegreeProgram(schema.type) && {
              selectedTitlePeriod: titlePeriods.findIndex((titlePeriod) => titlePeriod.fi === titlePeriodName),
              titleExtraPart
            })
          });
        } catch (e) {
          dispatchAddErrorMessage(e);
        } finally {
          dispatchSetTaskFulfilled();
          setLoaded(true);
        }
      };
      fetchData();
    } else {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (loaded && isDegreeProgramSurveyTitleUninitialised(surveyOptions)) {
      const subRoute = getSubRouteForSurveyType(surveyOptions.surveyType);
      const contentsRoute = getCompiledPath(subRoute.survey.edit.contents.index, { surveyId: surveyOptions.id });
      if (pathname !== contentsRoute) {
        navigate(contentsRoute, { replace: true });
      }
    }
  }, [pathname, loaded]);

  useTitle(t('editSurvey.title'));
  if (isInSubRoute) return <Outlet />;
  return +params.surveyId === surveyOptions.id && loaded ? (
    <TransitionGroup>
      <CSSTransition
        key={pathname}
        classNames="fade"
        timeout={300}
        exit={false}
      >
        <div className="page-container">
          <ScrollToTop />
          <h1 className="page-title--small">{t('editSurvey.title')}</h1>
          <Outlet />
        </div>
      </CSSTransition>
    </TransitionGroup>
  ) : null;
};

EditSurvey.propTypes = {
  surveyOptions: surveyOptionsType.isRequired,
  dispatchUpdate: func.isRequired,
  dispatchClear: func.isRequired,
  dispatchInitParticipants: func.isRequired,
  ...langType
};

const mapStateToProps = ({ survey }) => ({
  surveyOptions: survey.surveyOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdate: (surveyOptions) =>
    dispatch(updateSurveyOptionsAction(surveyOptions)),
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled()),
  dispatchClear: () => dispatch(clearSurveyAction()),
  dispatchInitParticipants: (participants) =>
    dispatch(initSurveyParticipantsAction(participants))
});

export default withLang(connect(mapStateToProps, mapDispatchToProps)(EditSurvey));
